import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "@/lib/apiClient";
import { formatFinanceMoney } from "@/lib/financeFormat";
import type { FinanceInvoice, FinanceSummary } from "@/lib/financeTypes";
import { FinanceErrorBanner, FinanceStatusBadge } from "@/lib/financeUi";
import {
  ArrowDownRight,
  ArrowUpRight,
  DollarSign,
  Receipt,
  TrendingDown,
  TrendingUp,
} from "lucide-react";

type InvoicesResponse = { invoices?: FinanceInvoice[] };

type TabId = "dashboard" | "getting-started" | "updates";

export function FinanceOverviewPage() {
  const [tab, setTab] = useState<TabId>("dashboard");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [invoices, setInvoices] = useState<FinanceInvoice[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const [summaryRes, invoicesRes] = await Promise.all([
        apiGet<FinanceSummary>("/api/finance/summary"),
        apiGet<InvoicesResponse>("/api/finance/invoices?limit=10"),
      ]);
      if (cancelled) return;
      if (!summaryRes.ok) {
        setError(summaryRes.error || "Could not load finance summary.");
        setSummary(null);
        setInvoices([]);
      } else {
        setError(null);
        setSummary(summaryRes.data ?? null);
        setInvoices(invoicesRes.ok ? (invoicesRes.data?.invoices ?? []) : []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const directoryTotal =
    summary && typeof summary.customerCount === "number" && typeof summary.vendorCount === "number"
      ? summary.customerCount + summary.vendorCount
      : null;

  const tabs: { id: TabId; label: string }[] = [
    { id: "dashboard", label: "Dashboard" },
    { id: "getting-started", label: "Getting started" },
    { id: "updates", label: "Recent updates" },
  ];

  return (
    <div className="space-y-6 p-6 sm:p-8">
      <div className="border-b border-slate-200/80 pb-4 dark:border-white/10">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Finance</p>
        <h1 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-white">Books workspace</h1>
      </div>

      <div className="flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1 dark:border-white/10 dark:bg-slate-900/40">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              tab === t.id
                ? "bg-white text-slate-900 shadow-sm dark:bg-slate-950 dark:text-white"
                : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? <p className="text-sm text-slate-500">Loading finance data…</p> : null}
      {error ? <FinanceErrorBanner message={error} /> : null}

      {tab === "dashboard" && summary && !error ? (
        <div className="space-y-6">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                label: "Paid (total)",
                value: formatFinanceMoney(summary.paidTotal ?? 0),
                hint: "From payments received",
                icon: DollarSign,
                tone: "text-emerald-600",
              },
              {
                label: "Open AR",
                value: formatFinanceMoney(summary.openTotal ?? 0),
                hint: "Unpaid / partial invoices",
                icon: TrendingUp,
                tone: "text-blue-600",
              },
              {
                label: "Overdue",
                value: String(summary.overdueCount ?? 0),
                hint: "Past due invoices",
                icon: TrendingDown,
                tone: "text-orange-600",
                hintIcon: ArrowDownRight,
              },
              {
                label: "Directory",
                value: directoryTotal === null ? "—" : String(directoryTotal),
                hint: "Accounts + vendors",
                icon: Receipt,
                tone: "text-violet-600",
                hintIcon: ArrowUpRight,
              },
            ].map((card) => (
              <div
                key={card.label}
                className="rounded-lg border border-slate-200/90 bg-white px-4 py-3 shadow-sm dark:border-white/10 dark:bg-slate-950/40"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {card.label}
                  </p>
                  <card.icon className={`h-4 w-4 ${card.tone}`} aria-hidden />
                </div>
                <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900 dark:text-white">{card.value}</p>
                <p className="flex items-center text-xs text-slate-500 dark:text-slate-400">
                  {card.hintIcon ? <card.hintIcon className="mr-1 h-3 w-3" aria-hidden /> : null}
                  {card.hint}
                </p>
              </div>
            ))}
          </div>

          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-950/40">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3 dark:border-white/10">
              <div>
                <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Recent invoices</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">Latest records in this organization.</p>
              </div>
              <Link
                to="/finance/sales/invoices"
                className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
              >
                View all
              </Link>
            </div>
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500 dark:border-white/10 dark:bg-slate-900/50">
                <tr>
                  <th className="px-4 py-3 font-semibold">Number</th>
                  <th className="px-4 py-3 font-semibold">Account</th>
                  <th className="px-4 py-3 font-semibold">Amount</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Due</th>
                  <th className="px-4 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                      No invoices yet. Create one from Sales → Invoices → New invoice.
                    </td>
                  </tr>
                ) : (
                  invoices.map((inv) => (
                    <tr key={inv.id} className="border-b border-slate-100 dark:border-white/5">
                      <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">
                        <Link
                          to={`/finance/sales/invoices/${encodeURIComponent(inv.id)}`}
                          className="text-indigo-600 hover:underline dark:text-indigo-300"
                        >
                          {inv.number || inv.id}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{inv.customerName?.trim() || "—"}</td>
                      <td className="px-4 py-3 tabular-nums text-slate-600 dark:text-slate-300">
                        {typeof inv.amount === "number" ? formatFinanceMoney(inv.amount, inv.currency || "USD") : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <FinanceStatusBadge status={inv.status} />
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{inv.dueDate || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/finance/sales/invoices/${encodeURIComponent(inv.id)}`}
                          className="rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {tab === "getting-started" ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-950/40">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Getting started</h2>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Phase 1 Books path: account → invoice → payment.
          </p>
          <ol className="mt-4 space-y-2 text-sm text-slate-600 dark:text-slate-400">
            <li>
              1.{" "}
              <Link to="/finance/sales/accounts" className="text-indigo-600 underline dark:text-indigo-300">
                Add an account
              </Link>
              .
            </li>
            <li>
              2.{" "}
              <Link to="/finance/sales/invoices/new" className="text-indigo-600 underline dark:text-indigo-300">
                Create an invoice
              </Link>
              .
            </li>
            <li>
              3.{" "}
              <Link to="/finance/sales/payments-received" className="text-indigo-600 underline dark:text-indigo-300">
                Record a payment received
              </Link>
              .
            </li>
          </ol>
        </div>
      ) : null}

      {tab === "updates" ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-950/40">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Recent updates</h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">No system events yet.</p>
        </div>
      ) : null}
    </div>
  );
}
